import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useUserContext from 'Context/UserContext';
import useComponentDidMount from 'Hooks/useComponentDidMount';

const NewClientContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const CLIENT_REFRESH_STARTED = 'client-refresh-started';
export const CLIENT_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
export const CLIENT_REFRESH_FAILED = 'client-refresh-failed';

// Initialize State
const init = () => {
  // IMPORTANT: Please be cautious of adding additional state here.
  // Is there a need for it to be loaded concurrently with the client?
  // If not, it may make sense in a separate context.
  return {
    isInitializing: true,
    initializingError: null,
    clientResource: null,
    isClientRefreshing: false,
    clientRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        clientResource: action.clientResource,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case CLIENT_REFRESH_STARTED: {
      return {
        ...state,
        isClientRefreshing: true,
        clientRefreshError: null,
      };
    }
    case CLIENT_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isClientRefreshing: false,
        clientRefreshError: null,
        clientResource: action.clientResource,
      };
    }
    case CLIENT_REFRESH_FAILED: {
      return {
        ...state,
        isClientRefreshing: false,
        clientRefreshError: action.clientRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled NewClientContext action type: ${action.type}`);
    }
  }
};

const NewClientContextProvider = ({
  children,
}) => {
  const { userResource } = useUserContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshClientResource = React.useCallback(async () => {
    try {
      dispatch({ type: CLIENT_REFRESH_STARTED });
      const [clientRes] = await userResource.relationship(fr.relationships.client.name).link('related').get();
      dispatch({ type: CLIENT_REFRESH_SUCCEEDED, clientResource: clientRes });
    } catch (err) {
      dispatch({ type: CLIENT_REFRESH_FAILED, clientRefreshError: err });
    }
  }, [userResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      clientResource: state.clientResource,
      isClientRefreshing: state.isClientRefreshing,
      clientRefreshError: state.clientRefreshError,
      refreshClientResource,
    };
  }, [state, refreshClientResource]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });
      const [clientResp] = await Promise.all([
        userResource.relationship(fr.relationships.client.name).link('related').get(),
      ]);

      const [clientRes] = clientResp;

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        clientResource: clientRes,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <NewClientContext.Provider value={memoizedValue}>
      {children}
    </NewClientContext.Provider>
  );
};

const useNewClientContext = () => {
  const context = React.useContext(NewClientContext);
  if (context === undefined) {
    throw new Error('useNewClientContext must be used within a NewClientContextProvider');
  }
  return context;
};

NewClientContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  NewClientContextProvider,
  useNewClientContext,
};
